import React, { Fragment, useState, useEffect, useRef } from 'react';
import qs from 'qs';
import { useRefinementList } from 'react-instantsearch';
import RefinementTag from './RefinementTag';
import { updateSearchRefFromUrl } from './utils';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { getSession, SessionStorageKeys } from '@jbhi-fi/utils/src/sessionStorage';
import { postAlgoliaFilterClick, postAlgoliaFilterView } from '../../../utils/algolia/search-insights';
import { AlgoliaApiClient } from '../../../api/localisation/AlgoliaApiClient';
import { useQuery } from 'react-query';

interface CheckboxRefinementProps {
  facet: string;
  limit: number;
  showMoreLimit: number;
  setDisableScroll: any;
  trackingInfo: any;
  onDropdownToggle?: (isOpen: boolean) => void;
}

type RefinementListSortBy = 'count:asc' | 'count:desc' | 'name:asc' | 'name:desc';

type CustomRefinementListSortBy = RefinementListSortBy | 'numeric:asc' | 'numeric:desc';

/**
 * Facet Sorting Config
 * Map so we can retrieve the sort order using the facet name directly
 */
const facetSortingConfig: Map<string, CustomRefinementListSortBy> = new Map([
  ...(window.Jbhifi.facetSorting?.countAscending?.map((item: string) => [item.trim(), 'count:asc']) || []),
  ...(window.Jbhifi.facetSorting?.nameAscending?.map((item: string) => [item.trim(), 'name:asc']) || []),
  ...(window.Jbhifi.facetSorting?.nameDescending?.map((item: string) => [item.trim(), 'name:desc']) || []),
  ...(window.Jbhifi.facetSorting?.numericAscending?.map((item: string) => [item.trim(), 'numeric:asc']) || []),
  ...(window.Jbhifi.facetSorting?.numericDescending?.map((item: string) => [item.trim(), 'numeric:desc']) || []),
]);

const ON_DISPLAY_FACET = 'Display Models In-Store';

/**
 * Function for to create checkboxes based on the items and facets
 */
const makeCheckboxItems = (items: any[], filterSearchTerm: string, sortOrder: CustomRefinementListSortBy) => {
  const checkboxItems = filterSearchTerm
    ? items.filter(s => s.label.toLowerCase().startsWith(filterSearchTerm.trim().toLowerCase()))
    : items;

  if (!sortOrder.startsWith('numeric')) {
    return checkboxItems;
  }

  // Custom numerical sort using label
  return checkboxItems.sort((a, b) => {
    const refinedDiff = b.isRefined - a.isRefined; // Refined items first
    if (refinedDiff !== 0) return refinedDiff;

    const aValue = parseFloat(a.label);
    if (isNaN(aValue)) return 1;

    const bValue = parseFloat(b.label);
    if (isNaN(bValue)) return -1;

    if (sortOrder === 'numeric:asc') {
      return aValue - bValue;
    }

    return bValue - aValue;
  });
};

export const CheckboxRefinement = ({
  facet,
  limit,
  showMoreLimit,
  setDisableScroll,
  trackingInfo,
  onDropdownToggle,
}: CheckboxRefinementProps) => {
  const attribute = `facets.${facet}`;
  const sortOrder = facetSortingConfig.get(facet.toLowerCase()) || 'count:desc';

  const { items, refine } = useRefinementList({
    attribute,
    showMoreLimit,
    limit,
    sortBy: ['isRefined:desc', (sortOrder.startsWith('numeric') ? 'count:desc' : sortOrder) as RefinementListSortBy],
  });

  const [showCheckboxInput, setShowCheckboxInput] = useState(false);
  const RefineButton = useRef<HTMLButtonElement>(null);
  const RefineDropdown = useRef<HTMLDivElement>(null);
  const [checkedItems, setCheckedItems] = useState(items.filter(i => i.isRefined));
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const [clickType, setClickType] = useState('');

  const searchState = qs.parse(window.location.search.slice(1)) as any;
  const searchRef = useRef(searchState);
  const checkboxItems = makeCheckboxItems(items, filterSearchTerm, sortOrder);
  const isSomeRefined = items.filter(f => f.isRefined === true).length;

  let prependedLabel = '';

  const { data: storesData } = useQuery({
    queryKey: ['storesByIds', items.map(item => item.label)],
    queryFn: () => AlgoliaApiClient.getStoresByIds(items.map(item => item.label)),
    enabled: facet === ON_DISPLAY_FACET && items.length > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    // Add Inches to screen size facet
    if (facet === 'Screen Size (Inches)') {
      prependedLabel = '"';
    }
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, []);

  useEffect(() => {
    const updatedItems = makeCheckboxItems(items, filterSearchTerm, sortOrder);
    if (checkedItems.length === 0 && updatedItems.filter(i => i.isRefined).length > 0) {
      setCheckedItems(updatedItems.filter(i => i.isRefined));
    } else if (updatedItems.filter(i => i.isRefined).length === 0) {
      setCheckedItems([]);
    }
  }, [items, filterSearchTerm]);

  /**
   * Function to handle dropdown toggle (clicking button)
   */
  const toggleDropdown = event => {
    onDropdownToggle?.(!showCheckboxInput);
    pushToDataLayer({
      event: 'open_filter_group',
      filter_group: facet,
      search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
      search_term: searchState?.query || '',
    });
    postAlgoliaFilterView(facet);
    event.preventDefault();
    setFilterSearchTerm('');
    setShowCheckboxInput(!showCheckboxInput);
  };

  /**
   * Function to handle external clicks
   */
  const externalClick = event => {
    const Button = RefineButton.current;
    const Dropdown = RefineDropdown.current;

    // Close price range dropdown if clicked outside of target window (desktop)
    if (!event || (Dropdown && !Dropdown.contains(event.target) && Button && !Button.contains(event.target))) {
      document.body.removeEventListener('pointerdown', externalClick);
      setShowCheckboxInput(false);
      setDisableScroll(false);
    }
  };

  useEffect(() => {
    if (showCheckboxInput) {
      document.body.addEventListener('pointerdown', externalClick);
      setDisableScroll(true);
    } else {
      document.body.removeEventListener('pointerdown', externalClick);
      setDisableScroll(false);
    }
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, [showCheckboxInput]);

  const toggleSelect = e => {
    const filters = Object.keys(searchState)
      .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
      .reduce((obj, key) => {
        if (key.toLowerCase() === 'price') {
          obj[key.toLowerCase()] = { type: 'range', value: searchState[key] };
        } else {
          obj[key.toLowerCase()] = Array.isArray(searchState[key])
            ? searchState[key].map(i => ({ type: 'checkbox', value: i }))
            : { type: 'checkbox', value: searchState[key] };
        }
        return obj;
      }, {});

    updateSearchRefFromUrl(searchRef);

    if (isSomeRefined) {
      const checkedItems = checkboxItems.filter(i => i.isRefined);
      checkedItems.forEach(i => {
        refine(i.value);
      });

      pushToDataLayer({
        event: 'clear_filter_group',
        filter_group: facet,
        search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
        search_term: searchState?.query || '',
      });
      window.dataLayer.push({
        event: 'custom.filter.set',
        group: 'site search',
        category: 'search',
        label: 'Clear all',
        name: facet,
        value: 'On',
        info: {
          element: e.target.name,
          position: trackingInfo.position,
          sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
          hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
        },
        newFilters: {
          ...filters,
          [facet.toLowerCase()]: [],
        },
        oldFilters: filters,
      });
    } else {
      checkboxItems.forEach(i => {
        refine(i.value);
      });

      window.dataLayer.push({
        event: 'custom.filter.set',
        group: 'site search',
        category: 'search',
        label: 'Select all',
        name: facet,
        value: 'On',
        info: {
          element: e.target.name,
          position: trackingInfo.position,
          sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
          hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
        },
        newFilters: {
          ...filters,
          [facet.toLowerCase()]: checkboxItems.map(i => ({ type: 'checkbox', value: i.value[0] })),
        },
        oldFilters: filters,
      });
    }
  };

  const labelOnClickHandler = () => {
    setClickType('text');
  };

  const toggleCheck = (e, item) => {
    setClickType('checkbox');
    pushToDataLayer({
      event: 'select_filter',
      filter_group: facet,
      filter_name: 'undefined',
      filter_type: clickType,
      filter_value: item.label,
      search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
      search_term: searchState?.query || '',
    });
    postAlgoliaFilterClick(facet, item.label);
    e.persist();
    let values = '';

    const filters = Object.keys(searchState)
      .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
      .reduce((obj: any, key: string) => {
        if (key.toLowerCase() === 'price') {
          obj[key.toLowerCase()] = { type: 'range', value: searchState[key] };
        } else {
          obj[key.toLowerCase()] = Array.isArray(searchState[key])
            ? (searchState[key] as Array<string>).map(i => ({ type: 'checkbox', value: i }))
            : { type: 'checkbox', value: searchState[key] };
        }
        return obj;
      }, {});

    if (item.isRefined) {
      setCheckedItems([...checkedItems.filter(i => i.label !== item.label)]);
      pushToDataLayer({
        event: 'unselect_filter',
        filter_group: facet,
        filter_name: 'undefined',
        filter_type: clickType,
        filter_value: item.label,
        search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
        search_term: searchState?.query || '',
      });
    } else {
      setCheckedItems([...checkedItems.filter(i => i !== item), { ...item, isRefined: true }]);
    }

    if (Array.isArray(item.value) && item.value.length > 1) {
      if (item.isRefined && item.value.filter(i => i === item.label || i[0] === item.label).length > 0) {
        values = item.value.filter(i => i !== item.label && i[0] !== item.label);
      } else {
        values = item.value;
      }
    } else {
      values = item.value;
    }

    refine(values);
    updateSearchRefFromUrl(searchRef);

    window.dataLayer.push({
      event: 'custom.filter.set',
      group: 'site search',
      category: 'search',
      label: item.isRefined ? 'off' : 'on',
      name: facet,
      value: item.label,
      info: {
        element: e.target.name,
        position: trackingInfo.position,
        sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
        hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
      },
      newFilters: {
        ...filters,
        [facet.toLowerCase()]: values ? { type: 'checkbox', value: values } : undefined,
      },
      oldFilters: filters,
    });
  };

  if (!filterSearchTerm && checkboxItems.length <= 0) {
    return <></>;
  }

  const handleChange = event => {
    const { value } = event.target;
    setFilterSearchTerm(value);
  };

  if (items.length <= 1) return null;

  return (
    <div className="search-results-facet">
      <button
        type="button"
        className={`search-results-facet-button${showCheckboxInput ? ' active' : ''}${
          isSomeRefined > 0 ? ' refined' : ''
        }`}
        ref={RefineButton}
        onClick={toggleDropdown.bind(this)}
      >
        {facet}
        {isSomeRefined > 0 && ` (${isSomeRefined})`}
      </button>
      <div
        className={`search-results-facet-dropdown ais-RefinementList ${showCheckboxInput ? 'active' : ''}`}
        ref={RefineDropdown}
      >
        {checkedItems.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {checkedItems.map((item, index) => {
              if (item.isRefined) {
                return (
                  <RefinementTag
                    key={index}
                    label={storesData?.find(store => store.objectID === item.label)?.storeName ?? item.label}
                    handleTagRemoval={e =>
                      toggleCheck(
                        e,
                        checkboxItems.find(i => i.label === item.label)
                      )
                    }
                  />
                );
              }
            })}
          </div>
        )}
        {items.length >= 10 && (
          <input
            type="text"
            className="filter-search"
            value={filterSearchTerm}
            onChange={handleChange.bind(this)}
            placeholder="Search"
            autoComplete="true"
            autoCapitalize="none"
            style={!isSomeRefined ? { marginTop: '0.9375rem' } : null}
            autoFocus
          />
        )}
        <ul className="ais-RefinementList-list">
          {checkboxItems.length === 0 && <li style={{ padding: '20px', textAlign: 'center' }}>Nothing to refine!</li>}
          {checkboxItems.map((item, index) => (
            <li key={index} className="ais-RefinementList-item">
              <label className="ais-RefinementList-label">
                <input
                  className="ais-RefinementList-checkbox"
                  type="checkbox"
                  name={item.label}
                  value={item.value}
                  checked={checkedItems.some(i => i.label === item.label && i.isRefined)}
                  onChange={e => toggleCheck(e, item)}
                />
                <span onClick={labelOnClickHandler} className="ais-RefinementList-labelText">
                  <Fragment>
                    {storesData?.find(store => store.objectID === item.label)?.storeName ?? item.label + prependedLabel}
                  </Fragment>
                </span>
                <span className="ais-RefinementList-count">
                  <Fragment>{item.count}</Fragment>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
